import apiClient from './apiClient';

/**
 * Service to manage maintenance mode functionality
 */

// Cache for maintenance status
let maintenanceCache = {
    inMaintenance: false,
    lastChecked: null,
    ttl: 300000, // 5 minutes cache TTL
    bypassValidated: false,
    refreshing: false, // Flag to prevent multiple simultaneous refreshes
    backgroundRefreshIntervalId: null, // ID for background refresh interval
    lastError: null, // Track last error for better decision making
    consecutiveErrors: 0 // Count consecutive errors
};

// Internal function to check maintenance status
const checkMaintenanceStatus = async () => {
    if (maintenanceCache.refreshing) {
        return maintenanceCache.inMaintenance;
    }

    maintenanceCache.refreshing = true;

    try {
        const response = await apiClient.get('/core/maintenance-status/');
        maintenanceCache.inMaintenance = response.data.maintenance_mode;
        maintenanceCache.lastChecked = Date.now();
        maintenanceCache.refreshing = false;
        // Reset error tracking on successful request
        maintenanceCache.lastError = null;
        maintenanceCache.consecutiveErrors = 0;
        return maintenanceCache.inMaintenance;
    } catch (error) {
        console.error('Error checking maintenance status:', error);
        
        // Track error for better decision making
        maintenanceCache.lastError = error;
        maintenanceCache.consecutiveErrors++;
        
        // Solo asumir mantenimiento en casos muy específicos
        if (error.response) {
            const statusCode = error.response.status;
            
            // Solo asumir mantenimiento si:
            // 1. Es un 503 (Service Unavailable) que típicamente indica mantenimiento
            // 2. O si hay un mensaje específico que indique mantenimiento
            if (statusCode === 503) {
                console.warn(`Servidor retornó ${statusCode} (Service Unavailable), asumiendo modo mantenimiento`);
                maintenanceCache.inMaintenance = true;
                maintenanceCache.lastChecked = Date.now();
                maintenanceCache.refreshing = false;
                return true;
            }
            
            // Para otros errores (429, 500, 502, 504), no asumir mantenimiento
            // ya que pueden ser errores temporales de red o sobrecarga
            if (statusCode === 429 || (statusCode >= 500 && statusCode < 600)) {
                console.warn(`Servidor retornó ${statusCode}, pero no asumiendo mantenimiento automáticamente`);
            }
        }

        // Si tenemos información en caché, usarla
        if (maintenanceCache.lastChecked) {
            const cacheAge = Date.now() - maintenanceCache.lastChecked;
            const cacheAgeMinutes = Math.floor(cacheAge / 60000);
            console.warn(`Usando último estado conocido de mantenimiento (${cacheAgeMinutes} minutos de antigüedad)`);
            maintenanceCache.refreshing = false;
            return maintenanceCache.inMaintenance;
        }
        
        // Si no hay caché y no es un error 503, asumir que NO está en mantenimiento
        // Esto evita falsos positivos cuando hay problemas temporales de red
        console.warn('Sin información de caché disponible, asumiendo que NO está en mantenimiento');
        maintenanceCache.refreshing = false;
        return false;
    }
};

// Start background refresh to periodically check maintenance status
const startBackgroundRefresh = () => {
    // Clear previous interval if exists
    if (maintenanceCache.backgroundRefreshIntervalId) {
        clearInterval(maintenanceCache.backgroundRefreshIntervalId);
    }
    
    // Check every 4 minutes (slightly less than the TTL)
    maintenanceCache.backgroundRefreshIntervalId = setInterval(async () => {
        try {
            await checkMaintenanceStatus();
            console.log('[Background] Maintenance status refreshed at', new Date().toLocaleTimeString());
        } catch (error) {
            console.error('[Background] Error refreshing maintenance status:', error);
        }
    }, 240000); // 4 minutes
};

// Check if user has validated bypass
const hasValidatedBypass = () => {
    // Check memory cache first 
    if (maintenanceCache.bypassValidated) {
        return true;
    }

    // Check localStorage and verify if the validation hasn't expired
    if (localStorage.getItem('maintenance_bypass') === 'true') {
        const expiryTime = parseInt(localStorage.getItem('maintenance_bypass_expiry') || '0', 10);
        
        if (expiryTime && expiryTime > Date.now()) {
            // La validaci�n no ha expirado, actualizar la cache en memoria
            maintenanceCache.bypassValidated = true;
            return true;
        } else {
            // Validaci�n expirada, limpiar los datos
            clearBypassValidation();
            return false;
        }
    }
    
    return false;
};

// Clear bypass validation
const clearBypassValidation = () => {
    maintenanceCache.bypassValidated = false;
    localStorage.removeItem('maintenance_bypass');
    localStorage.removeItem('maintenance_bypass_timestamp');
    localStorage.removeItem('maintenance_bypass_expiry');
};

const maintenanceService = {
    /**
     * Check if the site is in maintenance mode
     * @param {boolean} forceCheck - Force a server check even if cache is valid
     * @returns {Promise<boolean>} - Whether the site is in maintenance mode
     */
    async isInMaintenance(forceCheck = false) {
        // If bypass has been validated, pretend site is not in maintenance
        if (hasValidatedBypass()) {
            return false;
        }
        
        const now = Date.now();
        const cacheValid = maintenanceCache.lastChecked && 
                          (now - maintenanceCache.lastChecked < maintenanceCache.ttl);
        
        // Si hay errores consecutivos, extender el TTL del caché para evitar spam de requests
        const extendedCacheValid = maintenanceCache.lastChecked && 
                                  maintenanceCache.consecutiveErrors > 2 &&
                                  (now - maintenanceCache.lastChecked < maintenanceCache.ttl * 2);
        
        // Use cached value if valid and not forcing a check
        if (!forceCheck && (cacheValid || extendedCacheValid)) {
            if (extendedCacheValid && !cacheValid) {
                console.log('Usando caché extendido debido a errores consecutivos');
            }
            return maintenanceCache.inMaintenance;
        }

        // If we need to force a check or cache is invalid
        if (cacheValid && forceCheck) {
            // If cache is valid but force check is requested, update in background
            // but return the cached value immediately
            checkMaintenanceStatus().catch(err => 
                console.error('Background maintenance check failed:', err)
            );
            return maintenanceCache.inMaintenance;
        }

        // If no valid cache, we have to wait for the check
        return checkMaintenanceStatus();
    },

    /**
     * Get maintenance status details
     * @returns {Promise<Object>} - Maintenance status details
     */
    async getMaintenanceStatus() {
        try {
            const response = await apiClient.get('/core/maintenance-status/');
            
            // Guardar el �ltimo estado conocido en cach�
            maintenanceCache.inMaintenance = response.data.maintenance_mode;
            maintenanceCache.lastChecked = Date.now();
            
            return {
                maintenance_mode: response.data.maintenance_mode,
                message: response.data.maintenance_message,
                has_bypass_password: response.data.has_bypass_password
            };
        } catch (error) {
            console.error('Error getting maintenance status:', error);
            if (maintenanceCache.lastChecked) {
                const cacheAge = Date.now() - maintenanceCache.lastChecked;
                const cacheAgeMinutes = Math.floor(cacheAge / 60000);
                const cacheAgeHours = Math.floor(cacheAge / 3600000);
                
                let ageText;
                if (cacheAgeMinutes < 1) {
                    ageText = 'menos de 1 minuto';
                } else if (cacheAgeMinutes < 60) {
                    ageText = `${cacheAgeMinutes} minuto${cacheAgeMinutes > 1 ? 's' : ''}`;
                } else {
                    ageText = `${cacheAgeHours} hora${cacheAgeHours > 1 ? 's' : ''}`;
                }
                
                return {
                    maintenance_mode: maintenanceCache.inMaintenance,
                    message: `Estado de mantenimiento desde caché (última verificación hace ${ageText}).`,
                    has_bypass_password: false
                };
            }
            
            // Si no hay caché, devolver un estado que indique que no se pudo verificar
            return {
                maintenance_mode: false, // Asumir que NO está en mantenimiento si no podemos verificar
                message: 'No se pudo verificar el estado de mantenimiento del servidor.',
                has_bypass_password: false
            };
        }
    },

    /**
     * Checks if a user should be allowed to bypass maintenance mode
     * @param {boolean} isAdmin - Whether the user is an admin
     * @returns {Promise<boolean>} - Whether the user can bypass maintenance
     */
    async canBypassMaintenance(isAdmin) {
        if (!await this.isInMaintenance(false)) {
            return true;
        }

        if (hasValidatedBypass()) {
            return true;
        }

        return isAdmin;
    },
    
    /**
     * Validate a bypass password for maintenance mode
     * @param {string} password - The password to validate
     * @param {number} expiryHours - Hours until the bypass validation expires (default: 12)
     * @returns {Promise<object>} - Response with validation result
     */
    async validateBypassPassword(password, expiryHours = 12) {
        try {
            const response = await apiClient.post('/core/validate-bypass-password/', { password });

            if (response.data.valid) {
                // Set the bypassValidated flag if password is valid
                maintenanceCache.bypassValidated = true;
                
                // Calculate expiry time (default: 12 hours)
                const expiryTime = Date.now() + (expiryHours * 60 * 60 * 1000);
                
                // Persist the validation state in localStorage with expiry
                localStorage.setItem('maintenance_bypass', 'true');
                localStorage.setItem('maintenance_bypass_expiry', expiryTime.toString());
                
                // Add expiry info to the response
                response.data.expiryTime = new Date(expiryTime).toLocaleString();
                response.data.message = `${response.data.message} (v�lido por ${expiryHours} horas)`;
            }

            return response.data;
        } catch (error) {
            console.error('Error validating bypass password:', error);
            return { valid: false, message: 'Error al validar la contrase�a' };
        }
    },

    /**
     * Checks if user has validated the bypass password
     * @returns {boolean} - Whether the user has validated the bypass password
     */
    hasValidatedBypass() {
        return hasValidatedBypass();
    },
    
    /**
     * Clear the bypass validation
     */
    clearBypassValidation() {
        clearBypassValidation();
    },

    /**
     * Get the last known maintenance state from cache
     * Useful when there's an error fetching the current state
     * @returns {Object} - Last known maintenance state
     */
    getLastKnownMaintenanceState() {
        return {
            inMaintenance: maintenanceCache.inMaintenance,
            lastChecked: maintenanceCache.lastChecked ? new Date(maintenanceCache.lastChecked) : null,
            consecutiveErrors: maintenanceCache.consecutiveErrors,
            lastError: maintenanceCache.lastError ? {
                status: maintenanceCache.lastError.response?.status,
                message: maintenanceCache.lastError.message
            } : null
        };
    },

    /**
     * Check if the site has a bypass password configured
     * @returns {Promise<boolean>} - Whether the site has a bypass password
     */
    async hasBypassPassword() {
        try {
            const response = await apiClient.get('/core/maintenance-status/');
            return response.data.has_bypass_password;
        } catch (error) {
            console.error('Error checking bypass password:', error);
            
            // Solo asumir que hay bypass password si es un error 503 o si ya tenemos validación
            if (error.response) {
                const statusCode = error.response.status;
                if (statusCode === 503) {
                    console.warn(`Servidor retornó ${statusCode} (Service Unavailable), asumiendo que hay bypass password disponible`);
                    return true;
                }
                
                // Para errores 429 o 5xx, ser más conservador
                if (statusCode === 429 || (statusCode >= 500 && statusCode < 600)) {
                    console.warn(`Servidor retornó ${statusCode}, verificando si hay bypass validado previamente`);
                    // Solo mostrar bypass si ya hay una validación previa
                    return this.hasValidatedBypass();
                }
            }
            
            // En caso de cualquier otro error, verificar si el usuario ya ha validado un bypass previamente
            if (this.hasValidatedBypass()) {
                return true;
            }
            
            return false;
        }
    },

    /**
     * Reset cache and check maintenance status
     * This is useful after logging in/out or changing maintenance settings
     * @param {boolean} preserveBypass - Whether to preserve bypass validation status
     */
    resetMaintenanceCache(preserveBypass = true) {
        // Si no queremos preservar el bypass, limpiamos todo
        if (!preserveBypass) {
            this.clearBypassValidation();
        }
        
        maintenanceCache = {
            ...maintenanceCache,
            lastChecked: null,
            bypassValidated: preserveBypass && this.hasValidatedBypass(),
            refreshing: false,
            lastError: null,
            consecutiveErrors: 0
        };
        
        // Restart background refresh
        startBackgroundRefresh();
        
        // Devolver una promesa para permitir encadenar acciones
        return checkMaintenanceStatus();
    },

    /**
     * Clean up background refresh interval when app is unmounted
     * Call this from your App.vue onBeforeUnmount() hook if needed
     */
    cleanupBackgroundRefresh() {
        if (maintenanceCache.backgroundRefreshIntervalId) {
            clearInterval(maintenanceCache.backgroundRefreshIntervalId);
            maintenanceCache.backgroundRefreshIntervalId = null;
            console.log('Maintenance background refresh stopped');
        }
    },
    
    /**
     * Manually update the maintenance status immediately
     * This can be useful when coming back from sleep/suspension
     */
    forceUpdate() {
        return checkMaintenanceStatus();
    }
};

// Start background refresh when this module is imported
if (typeof window !== 'undefined') { // Only in browser environment
    startBackgroundRefresh();
    
    // Force an initial check when the service is first loaded
    setTimeout(() => {
        checkMaintenanceStatus().catch(err =>
            console.error('Initial maintenance check failed:', err)
        );
    }, 1000);
}

export default maintenanceService;
